import { BffRequestHandler } from './helpers/BffRequestHandler';
import { getAttributionIDFromLocalStorage } from './utils/index';

export interface ClickDataInput {
    organization_id: string;
    is_server_side_enabled?: boolean;
    fbclid?: string;
    fbc?: string;
    fbp?: string;
    timestamp?: number;
}

export interface ClickData extends ClickDataInput {
    stable_id: string;
    user_agent: string;
}

// Create BFF request handler instance
// For listings-webapp, use empty baseUrl to make relative requests to same domain
const bffHandler = new BffRequestHandler('');

// Helper functions for time-boxed localStorage
const PENDING_CLICK_KEY = 'pendingClickData';

interface PendingClickData {
    orgId: string;
    timestamp: number;
}

export const storePendingClickInLocalStorage = (orgId: string): void => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            console.warn('localStorage not available for storing pending click data');
            return;
        }

        const pendingClickData: PendingClickData = {
            orgId,
            timestamp: Date.now(),
        };
        localStorage.setItem(PENDING_CLICK_KEY, JSON.stringify(pendingClickData));
    } catch (error) {
        console.warn('Failed to store pending click data in localStorage:', error);
    }
};

const getPendingClickFromLocalStorage = (): PendingClickData | null => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return null;
        }

        const stored = localStorage.getItem(PENDING_CLICK_KEY);
        if (!stored) return null;

        const data: PendingClickData = JSON.parse(stored);
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

        // If timestamp is older than 5 minutes ago, discard it
        if (data.timestamp < fiveMinutesAgo) {
            localStorage.removeItem(PENDING_CLICK_KEY);
            return null;
        }

        return data;
    } catch (error) {
        console.warn('Failed to parse pending click data from localStorage:', error);
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.removeItem(PENDING_CLICK_KEY);
        }
        return null;
    }
};

const clearPendingClickFromLocalStorage = (): void => {
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            localStorage.removeItem(PENDING_CLICK_KEY);
        }
    } catch (error) {
        console.warn('Failed to clear pending click data from localStorage:', error);
    }
};

// Helper function to check and save pending click if consent is granted
export const checkAndSavePendingClick = async (
    canStoreAttributionData?: boolean,
    isOptInJurisdiction?: boolean,
    isConsentDecisionMade?: boolean,
): Promise<void> => {
    const pendingData = getPendingClickFromLocalStorage();
    if (!pendingData) return;

    // If consent parameters are provided, check them
    if (
        canStoreAttributionData !== undefined &&
        isOptInJurisdiction !== undefined &&
        isConsentDecisionMade !== undefined
    ) {
        if (!(isOptInJurisdiction && canStoreAttributionData && isConsentDecisionMade)) {
            return; // Consent not granted
        }
    }

    const stableId = getAttributionIDFromLocalStorage();
    if (!stableId) {
        console.log('No stable_id available for saving pending click data');
        return;
    }

    try {
        await savePendingClickData(stableId, pendingData.orgId);
        clearPendingClickFromLocalStorage();
    } catch (error) {
        console.warn('Failed to save pending click data:', error);
    }
};

// Helper function to send click data to attribution service
export const sendClickDataToAttributionService = async (clickData: ClickData): Promise<void> => {
    try {
        // Transform the data to match the expected ClickIn interface
        const requestBody = {
            fbclid: clickData.fbclid,
            stable_id: clickData.stable_id,
            org_id: clickData.organization_id,
            fbc: clickData.fbc,
            fbp: clickData.fbp,
            user_agent: clickData.user_agent,
        };

        await bffHandler.request('/conversions-manager/api/v1/attribution/click', {
            method: 'POST',
            body: JSON.stringify(requestBody),
        });
    } catch (error) {
        console.warn('Attribution service error:', error);
        // Don't throw - attribution is non-blocking
    }
};

// Helper function to store pending click data while awaiting consent
export const storePendingClickData = async (clickData: ClickData): Promise<void> => {
    try {
        // Transform the data to match the expected ClickIn interface
        const requestBody = {
            fbclid: clickData.fbclid,
            stable_id: clickData.stable_id,
            org_id: clickData.organization_id,
            fbc: clickData.fbc,
            fbp: clickData.fbp,
            user_agent: clickData.user_agent,
        };

        await bffHandler.request('/conversions-manager/api/v1/attribution/pending-click/store', {
            method: 'POST',
            body: JSON.stringify(requestBody),
        });
    } catch (error) {
        console.warn('Pending click storage error:', error);
        // Don't throw - attribution is non-blocking
    }
};

// Helper function to save pending click data after consent is granted
export const savePendingClickData = async (
    stableId: string,
    organizationId: string,
): Promise<void> => {
    try {
        const requestBody = {
            stable_id: stableId,
            org_id: organizationId,
        };

        await bffHandler.request('/conversions-manager/api/v1/attribution/pending-click/save', {
            method: 'POST',
            body: JSON.stringify(requestBody),
        });
    } catch (error) {
        console.warn('Pending click save error:', error);
        // Don't throw - attribution is non-blocking
    }
};

// Helper function to safely extract a cookie value by name
const getCookieValue = (cookieName: string): string | undefined => {
    if (typeof window === 'undefined' || !document.cookie) {
        return undefined;
    }

    try {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i]?.trim();
            if (cookie && cookie.startsWith(`${cookieName}=`)) {
                const value = cookie.substring(cookieName.length + 1);
                return decodeURIComponent(value);
            }
        }
    } catch (error) {
        console.warn(`Failed to extract cookie ${cookieName}:`, error);
    }

    return undefined;
};

// Helper function to extract Facebook click IDs from URL and cookies
export const extractFacebookClickIds = (): {
    fbclid?: string;
    fbc?: string;
    fbp?: string;
} => {
    if (typeof window === 'undefined') {
        return {};
    }

    // Extract fbclid from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid') || undefined;

    // Extract fbc and fbp from cookies
    const fbc = getCookieValue('_fbc');
    const fbp = getCookieValue('_fbp');

    return {
        fbclid,
        fbc,
        fbp,
    };
};
